import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { FileDown, Users, Calendar, Loader2, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOIS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' }
];

const SyntheseGenerator = ({ open, onOpenChange }) => {
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [selectedBeneficiaires, setSelectedBeneficiaires] = useState([]);
  const [moisDebut, setMoisDebut] = useState(new Date().getMonth() + 1);
  const [anneeDebut, setAnneeDebut] = useState(new Date().getFullYear());
  const [moisFin, setMoisFin] = useState(new Date().getMonth() + 1);
  const [anneeFin, setAnneeFin] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState(false);
  const [mode, setMode] = useState('individual'); // 'individual' ou 'grouped'

  useEffect(() => {
    if (open) {
      loadBeneficiaires();
    }
  }, [open]);

  const loadBeneficiaires = async () => {
    try {
      const response = await api.get('/apprenants');
      setBeneficiaires(response.data.filter(b => b.actif !== false));
    } catch (error) {
      toast.error('Erreur de chargement des bénéficiaires');
    }
  };

  const handleSelectAll = () => {
    if (selectedBeneficiaires.length === beneficiaires.length) {
      setSelectedBeneficiaires([]);
    } else {
      setSelectedBeneficiaires(beneficiaires.map(b => b.id));
    }
  };

  const handleToggleBeneficiaire = (id) => {
    setSelectedBeneficiaires(prev => 
      prev.includes(id) 
        ? prev.filter(bId => bId !== id)
        : [...prev, id]
    );
  };

  const getYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  };

  const handleGenerate = async () => {
    if (selectedBeneficiaires.length === 0) {
      toast.error('Veuillez sélectionner au moins un bénéficiaire');
      return;
    }

    setGenerating(true);
    const token = localStorage.getItem('token');

    try {
      if (mode === 'individual') {
        // Générer individuellement pour chaque bénéficiaire
        for (const beneficiaireId of selectedBeneficiaires) {
          const beneficiaire = beneficiaires.find(b => b.id === beneficiaireId);
          
          const response = await fetch(
            `${API_URL}/api/reporting/synthese-individuelle/${beneficiaireId}/${moisDebut}/${anneeDebut}?mois_fin=${moisFin}&annee_fin=${anneeFin}`,
            {
              method: 'GET',
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );

          if (!response.ok) throw new Error(`Erreur pour ${beneficiaire?.nom}`);

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `synthese_${beneficiaire?.nom}_${moisDebut}_${anneeDebut}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }

        toast.success(`${selectedBeneficiaires.length} synthèse(s) générée(s)`);
      } else {
        // Générer une synthèse groupée
        const response = await fetch(
          `${API_URL}/api/reporting/synthese-groupee`,
          {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              beneficiaire_ids: selectedBeneficiaires,
              mois_debut: moisDebut,
              annee_debut: anneeDebut,
              mois_fin: moisFin,
              annee_fin: anneeFin
            })
          }
        );

        if (!response.ok) throw new Error('Erreur de génération');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `synthese_groupee_${moisDebut}_${anneeDebut}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success('Synthèse groupée générée avec succès');
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Erreur génération:', error);
      toast.error(error.message || 'Erreur de génération des synthèses');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <FileDown className="w-5 h-5 text-blue-600" />
            Générer des synthèses PDF
          </DialogTitle>
          <DialogDescription>
            Sélectionnez les bénéficiaires et la période pour générer les synthèses
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Mode de génération */}
          <div className="space-y-2">
            <Label>Mode de génération</Label>
            <div className="flex gap-4">
              <Button
                variant={mode === 'individual' ? 'default' : 'outline'}
                onClick={() => setMode('individual')}
                className="flex-1 rounded-xl"
              >
                <Users className="w-4 h-4 mr-2" />
                Individuelles
              </Button>
              <Button
                variant={mode === 'grouped' ? 'default' : 'outline'}
                onClick={() => setMode('grouped')}
                className="flex-1 rounded-xl"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Groupée
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              {mode === 'individual' 
                ? 'Un fichier PDF par bénéficiaire sélectionné'
                : 'Un seul PDF contenant toutes les synthèses'}
            </p>
          </div>

          {/* Sélection de période */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Période
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 p-3 bg-slate-50 rounded-xl">
                <Label className="text-xs text-slate-500">Du</Label>
                <div className="flex gap-2">
                  <Select value={moisDebut.toString()} onValueChange={(v) => setMoisDebut(parseInt(v))}>
                    <SelectTrigger className="rounded-xl flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MOIS.map(m => (
                        <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={anneeDebut.toString()} onValueChange={(v) => setAnneeDebut(parseInt(v))}>
                    <SelectTrigger className="rounded-xl w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getYears().map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2 p-3 bg-slate-50 rounded-xl">
                <Label className="text-xs text-slate-500">Au</Label>
                <div className="flex gap-2">
                  <Select value={moisFin.toString()} onValueChange={(v) => setMoisFin(parseInt(v))}>
                    <SelectTrigger className="rounded-xl flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MOIS.map(m => (
                        <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={anneeFin.toString()} onValueChange={(v) => setAnneeFin(parseInt(v))}>
                    <SelectTrigger className="rounded-xl w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getYears().map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Sélection des bénéficiaires */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Bénéficiaires ({selectedBeneficiaires.length}/{beneficiaires.length})
              </Label>
              <Button variant="ghost" size="sm" onClick={handleSelectAll} className="text-blue-600">
                {selectedBeneficiaires.length === beneficiaires.length ? (
                  <>
                    <Square className="w-4 h-4 mr-1" />
                    Désélectionner tout
                  </>
                ) : (
                  <>
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Tout sélectionner
                  </>
                )}
              </Button>
            </div>
            <ScrollArea className="h-[200px] border rounded-xl p-2">
              <div className="space-y-1">
                {beneficiaires.map((beneficiaire) => (
                  <div
                    key={beneficiaire.id}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors ${
                      selectedBeneficiaires.includes(beneficiaire.id) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleToggleBeneficiaire(beneficiaire.id)}
                  >
                    <Checkbox 
                      checked={selectedBeneficiaires.includes(beneficiaire.id)}
                      onCheckedChange={() => handleToggleBeneficiaire(beneficiaire.id)}
                    />
                    <div className="flex-1">
                      <span className="font-medium text-slate-900">
                        {beneficiaire.prenom} {beneficiaire.nom}
                      </span>
                      {beneficiaire.zone_geographique && (
                        <span className="text-xs text-slate-500 ml-2">
                          ({beneficiaire.zone_geographique})
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">
            Annuler
          </Button>
          <Button 
            onClick={handleGenerate} 
            disabled={generating || selectedBeneficiaires.length === 0}
            className="rounded-xl bg-blue-600 hover:bg-blue-700"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération...
              </>
            ) : (
              <>
                <FileDown className="w-4 h-4 mr-2" />
                Générer ({selectedBeneficiaires.length})
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyntheseGenerator;
